import { useEffect } from 'react';

// Never put authenticated travel chats into a shared seven-day localStorage
// key. A different account using the same device must not inherit them.
const LEGACY_KEY = 'travelai_chat_v1';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const storageKey = (userId) => userId ? `travelai_chat_v2:${encodeURIComponent(String(userId))}` : null;

export function loadPersistedChat(userId) {
  const key = storageKey(userId);
  if (!key) return null;
  try {
    const raw = window.sessionStorage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.savedAt)
        || parsed.savedAt > Date.now()
        || Date.now() - parsed.savedAt > MAX_AGE_MS
        || !Array.isArray(parsed?.messages)) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return {
      ...parsed,
      messages: parsed.messages.map((m) => ({
        ...m,
        timestamp: m.timestamp && Number.isFinite(new Date(m.timestamp).getTime())
          ? new Date(m.timestamp) : new Date(),
      })),
    };
  } catch {
    return null;
  }
}

export function clearPersistedChat(userId) {
  try {
    const key = storageKey(userId);
    if (key) window.sessionStorage?.removeItem(key);
    // Purge unscoped chat data left on shared devices by the older version.
    window.localStorage?.removeItem(LEGACY_KEY);
  } catch { /* private mode or denied browser storage */ }
}

/**
 * A same-tab supplier handoff can restore a chat, but another signed-in user
 * cannot read it. Nothing is persisted for an unauthenticated identity.
 */
export function useChatPersistence({
  userId, messages, currentTripPlan, currentHotels, currentFlights, sessionId,
}) {
  useEffect(() => {
    try { window.localStorage?.removeItem(LEGACY_KEY); }
    catch { /* legacy cleanup is best effort */ }
  }, []);

  useEffect(() => {
    const key = storageKey(userId);
    if (!key) return;
    try {
      const onlyWelcome = messages.length === 1 && !messages[0]?.isUser;
      if (onlyWelcome && !currentTripPlan && !currentHotels.length && !currentFlights.length) {
        window.sessionStorage?.removeItem(key);
        return;
      }
      window.sessionStorage?.setItem(key, JSON.stringify({
        messages, currentTripPlan, currentHotels, currentFlights, sessionId,
        savedAt: Date.now(),
      }));
    } catch { /* Session storage is optional, not required for chat. */ }
  }, [userId, messages, currentTripPlan, currentHotels, currentFlights, sessionId]);
}
